import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { useNavigate } from "@tanstack/react-router"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { FilePondDocumentUpload } from "@/components/file-pond-upload"
import { RequirementType } from "@/utils/membership/types"
import { useDeleteFile } from "@/utils/api/file-service"
import type { StepRequirementSummary } from "@/utils/membership/types"
import {
  useIsSavingMembershipApplicationDraft,
  useMembershipApplicationDraft,
  useSaveMembershipApplicationDraft,
  type MembershipApplicationRequirementsDraft,
} from "../api/useMembershipApplicationDraft"
import { membershipApplicationSteps } from "../constants"
import { useApplicationSectionConfiguration } from "../hooks/useApplicationConfiguration"
import { getCategoryNonDocumentRequirements } from "../data/category-requirements"


/**
 * Build dynamic zod schema based on requirements
 */
function buildRequirementsSchema(requirements: StepRequirementSummary[]) {
  const shape: Record<string, z.ZodTypeAny> = {}

  for (const req of requirements) {
    const fieldKey = `req_${req.id ?? req.label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`

    switch (req.type) {
      case RequirementType.TEXT:
        shape[fieldKey] = req.mandatory
          ? z.string().trim().min(1, `${req.label} is required`)
          : z.string().trim().optional()
        break

      case RequirementType.NUMBER:
        shape[fieldKey] = req.mandatory
          ? z.coerce.number().min(0, `${req.label} must be a positive number`)
          : z.coerce.number().min(0).optional()
        break

      case RequirementType.CHECKBOX:
        if (req.mandatory) {
          shape[fieldKey] = z.boolean().refine((val) => val === true, {
            message: `You must confirm ${req.label}`,
          })
        } else {
          shape[fieldKey] = z.boolean().optional()
        }
        break

      case RequirementType.FILE:
        shape[fieldKey] = req.mandatory
          ? z
              .string()
              .trim()
              .min(1, `${req.label} is required`)
          : z.string().trim().optional()
        break

      default:
        // For FILE and DROPDOWN, handle in their specific sections
        break
    }
  }

  return z.object(shape)
}

type RequirementsFormValues = Record<string, string | number | boolean | undefined>

function mapDraftToFormValues(
  draft?: MembershipApplicationRequirementsDraft,
): RequirementsFormValues {
  return draft ?? {}
}

function mapFormValuesToDraft(values: RequirementsFormValues): MembershipApplicationRequirementsDraft {
  return values
}

interface RequirementFileValue {
  id?: string
  fileId?: string
  fileUrl?: string
  originalFilename?: string
}

function parseRequirementFileValue(value: unknown): RequirementFileValue | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  if (!trimmed) return null
  try {
    const parsed = JSON.parse(trimmed) as RequirementFileValue
    if (parsed && typeof parsed === "object") {
      return parsed
    }
  } catch (_error) {
    return null
  }
  return null
}

export function RequirementsStep() {
  const navigate = useNavigate()
  const { data: draft } = useMembershipApplicationDraft()
  const saveDraftMutation = useSaveMembershipApplicationDraft()
  const isSavingDraft = useIsSavingMembershipApplicationDraft()
  const deleteFileMutation = useDeleteFile()

  const categoryId = draft?.category?.categoryId ?? null

  const {
    nextStep,
    categoriesQuery,
    category,
  } = useApplicationSectionConfiguration("requirements", categoryId)

  // Get all non-document requirements from the selected category
  const allRequirements = useMemo(() => {
    if (!category) return []
    return getCategoryNonDocumentRequirements(category)
  }, [category])

  const hasRequirements = allRequirements.length > 0

  useEffect(() => {
    if (categoriesQuery.isLoading) {
      return
    }
    if (!hasRequirements) {
      // Skip this step if no requirements
      const fallbackPath = nextStep?.path ?? membershipApplicationSteps[1]?.path
      if (fallbackPath) {
        navigate({ to: fallbackPath, replace: true })
      }
    }
  }, [categoriesQuery.isLoading, hasRequirements, nextStep, navigate])

  const formDefaultValues = useMemo(
    () => mapDraftToFormValues(draft?.requirements),
    [draft?.requirements],
  )

  const schema = useMemo(() => buildRequirementsSchema(allRequirements), [allRequirements])
  const resolver = useMemo(() => zodResolver(schema), [schema])

 const form = useForm<RequirementsFormValues>({
    resolver,
    defaultValues: formDefaultValues,
    mode: "onChange",
  })

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Form reset effect
  useEffect(() => {
    form.reset(formDefaultValues)
  }, [formDefaultValues, form])

  // Autosave effect
  useEffect(() => {
    let skipNextAutosave = true
    const subscription = form.watch((values, info) => {
      if (skipNextAutosave) {
        skipNextAutosave = false
        return
      }

      if (info?.type !== "change" && info?.type !== "blur") {
        return
      }

      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }

      autosaveTimerRef.current = setTimeout(() => {
        const parsed = schema.safeParse(values)
        if (!parsed.success) {
          return
        }

        const payload = mapFormValuesToDraft(parsed.data)
        saveDraftMutation.mutate({ requirements: payload })
      }, 800)
    })

    return () => {
      subscription.unsubscribe()
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nextStepPath =
    nextStep?.path ??
    membershipApplicationSteps.find((step) => step.id === "employment")?.path

  const [removingFieldKey, setRemovingFieldKey] = useState<string | null>(null)

  const handleRemoveFile = useCallback(
    async (
      fieldKey: string,
      fileValue: RequirementFileValue | null,
      onChange: (value: string | undefined) => void,
    ) => {
      try {
        setRemovingFieldKey(fieldKey)
        const fileId = fileValue?.fileId ?? fileValue?.id ?? null
        if (fileId) {
          await deleteFileMutation.mutateAsync({ fileId })
        }
        onChange("")
        form.clearErrors(fieldKey)
        await form.trigger(fieldKey)
        toast.success("File removed.")
      } catch (error) {
        console.error(error)
        toast.error("Unable to remove file. Please try again.")
      } finally {
        setRemovingFieldKey((current) => (current === fieldKey ? null : current))
      }
    },
    [deleteFileMutation, form],
  )

  const handleSubmit = useCallback(
    async (values: RequirementsFormValues) => {
      const payload = mapFormValuesToDraft(values)
      await saveDraftMutation.mutateAsync({ requirements: payload })

      if (nextStepPath) {
        navigate({ to: nextStepPath })
      }
    },
    [navigate, nextStepPath, saveDraftMutation],
  )

  const isBusy = isSavingDraft || saveDraftMutation.isPending

  if (!hasRequirements && !categoriesQuery.isLoading) {
    return null
  }

  /**
   * Render a requirement field based on its type
   */
  const renderRequirementField = (requirement: StepRequirementSummary) => {
    const fieldKey = `req_${requirement.id ?? requirement.label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`

    switch (requirement.type) {
      case RequirementType.TEXT:
        return (
          <FormField
            key={fieldKey}
            control={form.control}
            name={fieldKey}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel required={requirement.mandatory}>
                  {requirement.label}
                  {!requirement.mandatory && <span className="ml-1 text-muted-foreground">(Optional)</span>}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={`Enter ${requirement.label.toLowerCase()}`}
                    data-invalid={fieldState.invalid}
                    {...field}
                    value={field.value as string | undefined}
                  />
                </FormControl>
                {requirement.description && (
                  <FormDescription>{requirement.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case RequirementType.NUMBER:
        return (
          <FormField
            key={fieldKey}
            control={form.control}
            name={fieldKey}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel required={requirement.mandatory}>
                  {requirement.label}
                  {!requirement.mandatory && <span className="ml-1 text-muted-foreground">(Optional)</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    placeholder={requirement.description ?? "0"}
                    data-invalid={fieldState.invalid}
                    {...field}
                    value={field.value as number | undefined}
                  />
                </FormControl>
                {requirement.description && (
                  <FormDescription>{requirement.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case RequirementType.CHECKBOX:
        return (
          <FormField
            key={fieldKey}
            control={form.control}
            name={fieldKey}
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormControl>
                  <div className="flex items-start gap-3 rounded-md border border-border bg-muted/20 p-4">
                    <Checkbox
                      id={fieldKey}
                      checked={field.value as boolean | undefined}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                    <div className="space-y-1">
                      <FormLabel required={requirement.mandatory} htmlFor={fieldKey} className="text-sm font-medium">
                        {requirement.label}
                        {!requirement.mandatory && <span className="ml-1 text-muted-foreground">(Optional)</span>}
                      </FormLabel>
                      {requirement.description && (
                        <FormDescription>{requirement.description}</FormDescription>
                      )}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case RequirementType.FILE:
        return (
          <FormField
            key={fieldKey}
            control={form.control}
            name={fieldKey}
            render={({ field }) => {
              const storedFile = parseRequirementFileValue(field.value)
              const hasFile = Boolean(storedFile)
              const isRemoving = removingFieldKey === fieldKey && deleteFileMutation.isPending

              return (
                <FormItem>
                  <FormLabel required={requirement.mandatory}>
                    {requirement.label}
                    {!requirement.mandatory && <span className="ml-1 text-muted-foreground">(Optional)</span>}
                  </FormLabel>
                  {requirement.description && (
                    <FormDescription className="mb-2">{requirement.description}</FormDescription>
                  )}

                  {hasFile ? (
                    <div className="flex flex-col gap-3 rounded-md border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground">
                          {storedFile?.originalFilename || "Uploaded file"}
                        </p>
                        {storedFile?.fileUrl ? (
                          <a
                            href={storedFile.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-medium text-primary underline underline-offset-4"
                          >
                            View uploaded file
                          </a>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            File stored with your application.
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={isRemoving}
                          onClick={() => handleRemoveFile(fieldKey, storedFile, field.onChange)}
                        >
                          {isRemoving ? "Removing..." : "Remove file"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <FormControl>
                      <FilePondDocumentUpload
                        maxFileSize="20MB"
                        isAuthenticated={false}
                        usernameOrEmail={draft?.profile?.email ?? undefined}
                        required={requirement.mandatory}
                        onUploadComplete={(fileId, fileUrl, filename) => {
                          field.onChange(
                            JSON.stringify({
                              fileId,
                              fileUrl,
                              originalFilename: filename,
                            }),
                          )
                          form.clearErrors(fieldKey)
                        }}
                        onUploadError={(error) => {
                          form.setError(fieldKey, {
                            type: "manual",
                            message: error,
                          })
                        }}
                      />
                    </FormControl>
                  )}

                  <FormMessage />
                </FormItem>
              )
            }}
          />
        )

      default:
        return null
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {allRequirements.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Requirements</CardTitle>
              <CardDescription>
                Please complete the following requirements for the {category?.categoryName ?? "selected"} membership category.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {allRequirements.map(renderRequirementField)}
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button type="submit" disabled={isBusy}>
                {isBusy ? "Saving..." : "Continue"}
              </Button>
            </CardFooter>
          </Card>
        )}
      </form>
    </Form>
  )
}
